import { inAppIntegrationLoadingAtom } from '@/integrations/in-app-integration-atom';
import {
  type MedicalDirectorSidebar,
  PropertyBagValue,
} from '@/integrations/medical-director/medicalDirectorSidebar';
import {
  trackMedicalDirectorGetPatientSessionsFailed,
  trackMedicalDirectorSyncSessionsFailed,
} from '@/lib/analytics/scribe/medical-director';
import { isValidEHRPatientId } from '@/lib/utils';
import { getSessionsByEhrPatientId } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { type SessionsResponse } from '@heidi/session/contracts';
import { useLatestRef } from '@heidi/utils';
import { isSameDay, parseISO } from 'date-fns';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { type MdSession } from './types';
import { type PendingMdPatient } from './usePendingMdPatient';

type UseMdSessionSyncParams = {
  medicalDirector: MedicalDirectorSidebar | null;
  pendingPatient: PendingMdPatient;
  sessionId: string | undefined;
  session: MdSession;
  integrationPatientId: string | undefined;
  isCreatingSession: boolean;
  isUpdatingPatientDetails: boolean;
  isUpdatingSessionEHRDetails: boolean;
  createSession: () => Promise<unknown>;
};

export const useMdSessionSync = ({
  medicalDirector,
  pendingPatient,
  sessionId,
  session,
  integrationPatientId,
  isCreatingSession,
  isUpdatingPatientDetails,
  isUpdatingSessionEHRDetails,
  createSession,
}: UseMdSessionSyncParams) => {
  const router = useRouter();
  const setMedicalDirectorLoading = useSetAtom(inAppIntegrationLoadingAtom);

  const isSyncingRef = useRef(false);
  // Set when a sync call is skipped because another is in flight. The trigger
  // effect's narrow dep array won't fire again on its own to re-invoke us, so
  // we re-fire from the finally block instead.
  const needsResyncRef = useRef(false);
  // syncSessionData re-invokes itself via this ref at the end of an in-flight
  // sync. Declared before the callback so the closure captures it cleanly.
  const syncSessionDataRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // Mutation-pending flags are read via refs (not closure values) inside
  // syncSessionData so they reflect live state after awaits.
  const isCreatingSessionRef = useLatestRef(isCreatingSession);
  const isUpdatingPatientDetailsRef = useLatestRef(isUpdatingPatientDetails);
  const isUpdatingSessionEHRDetailsRef = useLatestRef(isUpdatingSessionEHRDetails);

  // Capture MD's current patient into the pending ref before any sync runs,
  // so a stale route patientId doesn't override the patient MD has opened.
  useEffect(() => {
    if (!medicalDirector) return;

    pendingPatient.captureInitial(() => {
      const value = medicalDirector.readPropertyBagValue(
        PropertyBagValue.CURRENT_PATIENT_ID
      );
      return isValidEHRPatientId(value) ? String(value) : null;
    });
  }, [medicalDirector, pendingPatient]);

  const syncSessionData = useCallback(async () => {
    const patientIdToSync = pendingPatient.get() || integrationPatientId;

    if (!sessionId || !session || !medicalDirector || !patientIdToSync) {
      return;
    }

    if (isSyncingRef.current) {
      logger.debug('[MD]', 'Sync already in progress, queueing follow-up');
      needsResyncRef.current = true;
      return;
    }

    const shouldCheckForExistingSession =
      !session.ehr_patient_id ||
      String(session.ehr_patient_id) !== String(patientIdToSync);

    if (!shouldCheckForExistingSession) {
      pendingPatient.clearIfMatches(String(patientIdToSync));
      return;
    }

    isSyncingRef.current = true;
    setMedicalDirectorLoading(true);
    // Declared outside the try so the finally block can see it.
    let lookupFailed = false;
    try {
      logger.debug('[MD]', 'Checking for existing sessions for patient', patientIdToSync);

      let sessionsForCurrentPatient: SessionsResponse = {
        count: 0,
        sessions: [],
        has_next_page: false,
      };
      try {
        const { data } = await getSessionsByEhrPatientId(
          patientIdToSync,
          EHRProvider.Medical_Director
        );
        sessionsForCurrentPatient = data;
      } catch (error) {
        lookupFailed = true;
        trackMedicalDirectorGetPatientSessionsFailed(
          error instanceof Error ? error.message : 'Unknown error'
        );
      }

      // Abort on lookup failure: continuing would fall into the empty-list
      // branch below and create a new session from a transient network
      // error, producing duplicate/wrong sessions for the same patient.
      if (lookupFailed) return;

      const isAnyMutationPending =
        isCreatingSessionRef.current ||
        isUpdatingPatientDetailsRef.current ||
        isUpdatingSessionEHRDetailsRef.current;

      if (sessionsForCurrentPatient?.sessions.length > 0) {
        const sortedSessions = sessionsForCurrentPatient.sessions.sort((a, b) => {
          return (
            new Date(String(b.created_time_local)).getTime() -
            new Date(String(a.created_time_local)).getTime()
          );
        });

        const mostRecentSession = sortedSessions.at(0);

        // Local-day comparison. `created_time_local` is a UTC-anchored ISO
        // string; parseISO + isSameDay compares calendar days in the user's
        // local timezone — which is what "is this session from today?"
        // means to the clinician. A UTC-day comparison would silently
        // misclassify sessions created across the local 10:00 AEST
        // boundary as "yesterday".
        const shouldOpenMostRecentSession =
          !!mostRecentSession?.created_time_local &&
          isSameDay(parseISO(String(mostRecentSession.created_time_local)), new Date());

        logger.debug(
          '[MD]',
          'Should open most recent session',
          shouldOpenMostRecentSession,
          'Session date:',
          mostRecentSession?.created_time_local
        );

        if (shouldOpenMostRecentSession) {
          await router.push(`/scribe/session/${mostRecentSession.session_id}`);
        } else if (!isAnyMutationPending) {
          logger.debug('[MD]', 'Creating new session - no recent session found');
          await createSession();
        }
      } else if (!isAnyMutationPending) {
        logger.debug('[MD]', 'Creating new session - no existing sessions found');
        await createSession();
      }
    } catch (error) {
      trackMedicalDirectorSyncSessionsFailed(
        error instanceof Error ? error.message : 'Unknown error'
      );
    } finally {
      setMedicalDirectorLoading(false);
      isSyncingRef.current = false;
      // On lookup failure, leave the pending patient set so the next sync
      // attempt can resume from where we left off.
      if (!lookupFailed) {
        pendingPatient.clearIfMatches(String(patientIdToSync));
      }
      // Drain queued re-sync requests. The latest callback identity is
      // pulled via syncSessionDataRef so we see fresh closure values.
      if (needsResyncRef.current) {
        needsResyncRef.current = false;
        void syncSessionDataRef.current();
      }
    }
    // Mutation-pending flags are read via refs (not closure values) so they
    // are intentionally absent from this dep array — including them would
    // reintroduce the infinite effect → mutation → effect loop.
  }, [
    sessionId,
    session,
    medicalDirector,
    integrationPatientId,
    setMedicalDirectorLoading,
    router,
    createSession,
    isCreatingSessionRef,
    isUpdatingPatientDetailsRef,
    isUpdatingSessionEHRDetailsRef,
    pendingPatient,
  ]);

  // Keep the ref pointing at the latest callback identity so the in-flight
  // resync path picks up fresh closure values.
  syncSessionDataRef.current = syncSessionData;

  // `medicalDirector` is in deps so the effect re-runs once the sidebar
  // finishes initializing — without it, an already-mismatched session loaded
  // before MD becomes available never triggers a sync (the patientId /
  // ehr_patient_id / sessionId values are unchanged across the render that
  // sets MD). MD is set once and never toggles, so this can't loop.
  useEffect(() => {
    void syncSessionDataRef.current();
  }, [
    integrationPatientId,
    session?.ehr_patient_id,
    sessionId,
    medicalDirector,
    syncSessionDataRef,
  ]);
};
